import React, { useState } from 'react'
import ProcessingModal from './ProcessingModal'
import { FaLock, FaLockOpen, FaTimes, FaMoneyBillWave } from 'react-icons/fa'
import { FaGear, FaPrint } from 'react-icons/fa6'
import ConfirmModal from './ConfirmModal'
import { useSessionReset } from '@renderer/stores/sessionState'
import PinModal from './PinModal'
import { ReprintModal } from './view'
import { useFullscreenManager, useResponsiveTier } from '@renderer/hooks'
import { capabilities, hasReprintSessionApi } from '@renderer/lib/runtime'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'

interface ControlButtonsProps {
  isRootPath: boolean
  setShowSettings: (show: boolean, fromGear?: boolean) => void
  hideExitButton?: boolean
}

const ControlButtons: React.FC<ControlButtonsProps> = ({
  isRootPath,
  setShowSettings,
  hideExitButton = false
}) => {
  const { t } = useTranslation()
  const [showExitModal, setShowExitModal] = useState(false)
  const [showSkipPaymentModal, setShowSkipPaymentModal] = useState(false)
  const [showReprintPinModal, setShowReprintPinModal] = useState(false) // State for Reprint PIN check
  const [showReprintModal, setShowReprintModal] = useState(false) // State for Reprint Modal
  const [isQuitting, setIsQuitting] = useState(false)
  const sessionReset = useSessionReset()
  const { primaryColor } = useConfigStore.getState().config?.theme || {
    primaryColor: '#3B82F6'
  }

  const [isReprintPaymentActive, setIsReprintPaymentActive] = useState(false)
  const [showReprintBypassPinModal, setShowReprintBypassPinModal] = useState(false)
  const location = useLocation()
  const isPostSessionAiPage = location.pathname.includes('/post-session-ai')
  const navigate = useNavigate()
  const { projectId } = useParams()
  const isPaymentPage = location.pathname.endsWith('/payment')
  const isCheckoutPage = location.pathname.endsWith('/checkout')
  const isResultPage =
    location.pathname.endsWith('/result') || location.pathname.endsWith('/post-session-ai')
  // Reprint button visible on Welcome (isRootPath) or Result page
  const showReprintButton = isRootPath || isResultPage

  const { password } = useSettingsStore()
  const { getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const {
    isFullscreen,
    isStartupGracePeriodActive,
    showSubmitPinModal,
    setShowSubmitPinModal,
    handleFullscreenToggle,
    handlePinSubmit
  } = useFullscreenManager()
  const { isCompact: tierCompact } = useResponsiveTier()
  const compactChrome = tierCompact || capabilities.isMobile
  /** Desktop / kiosk: slightly larger icons + hit targets (e.g. 1440×900) */
  const iconClass = compactChrome ? 'text-[1.25rem]' : 'text-[1.75rem]'
  const hitArea =
    'inline-flex items-center justify-center touch-manipulation ' +
    (compactChrome ? 'min-h-9 min-w-9' : 'min-h-11 min-w-11')

  /** Fullscreen + PIN: faint during normal use, clearer during startup grace; avoid opacity-10 (invisible on light UIs). */
  const fullscreenPinIdleOpacity =
    isFullscreen && password && !isStartupGracePeriodActive
  const fullscreenPinGraceOpacity = isFullscreen && password && isStartupGracePeriodActive

  React.useEffect(() => {
    const handleReprintPaymentState = (event: Event): void => {
      const customEvent = event as CustomEvent<{ visible: boolean }>
      setIsReprintPaymentActive(customEvent.detail.visible)
    }

    window.addEventListener('reprint-payment-state', handleReprintPaymentState)
    return (): void => {
      window.removeEventListener('reprint-payment-state', handleReprintPaymentState)
    }
  }, [])

  const hasReprintAPI = hasReprintSessionApi()

  const handleOpenSettings = (): void => {
    if (isFullscreen && password && !isStartupGracePeriodActive) {
      // If in fullscreen and PIN is set, require PIN to open settings
      setShowSettings(false)
      setShowSubmitPinModal(true)
    } else {
      // If not in fullscreen, or in fullscreen but no PIN set (new user), or during grace period, allow opening settings
      setShowSettings(true, true)
    }
  }

  const handleOpenReprint = (): void => {
    if (isFullscreen && password && !isStartupGracePeriodActive) {
      // If in fullscreen and PIN is set, require PIN to open reprint
      setShowReprintPinModal(true)
    } else {
      // If not in fullscreen, or in fullscreen but no PIN set, or during grace period, allow opening reprint
      setShowReprintModal(true)
    }
  }

  const handleSkipPayment = (pin: string): void => {
    if (pin === password) {
      // Check if it's booth-daughter or newspaper custom path
      const customPath = welcomeSettings?.customPath
      const { getPageSettings } = useConfigStore.getState()
      const filterSettings = getPageSettings('filter')
      const applyAfterCapture = filterSettings?.applyAfterCapture || false

      let nextPage: string


      if (customPath === 'booth-daughter') {
        nextPage = 'capture'
      } else if (welcomeSettings?.startWithPayment) {
        // When startWithPayment is true, check applyAfterCapture
        // If applyAfterCapture is true, filter comes AFTER capture, so skip filter → go to frame
        // If applyAfterCapture is false, filter comes BEFORE capture, so go to filter
        nextPage = applyAfterCapture ? 'frame' : 'filter'
      } else {
        nextPage = 'capture'
      }

      useSessionStateStore.getState().logActivity('Payment skipped by operator', { to: nextPage })
      navigate(`/${projectId}/${nextPage}`)
      setShowSkipPaymentModal(false)
    } else {
      cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
    }
  }

  const handleReprintBypass = (pin: string): void => {
    if (pin === password) {
      // Dispatch event to UserReprintModal
      const event = new CustomEvent('user-reprint-bypass')
      window.dispatchEvent(event)
      setShowReprintBypassPinModal(false)
    } else {
      cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
    }
  }

  return (
    <>
      {!isPostSessionAiPage ? (
        <>
          {/* Top chrome lives outside Welcome's ScaleToFit; no canvas counter-scale needed here. */}
          <div
        className="pointer-events-none absolute top-0 right-0 left-0 z-[100] flex justify-end items-start"
        style={
          capabilities.safeAreaInsets ? { paddingTop: 'env(safe-area-inset-top)' } : undefined
        }
      >
        <div className="pointer-events-auto relative flex h-auto items-center py-0.5">
          <div
            className={`flex h-full items-center rounded-bl-md ${compactChrome ? 'gap-0 px-2 py-1' : 'gap-1 px-3 py-1.5'}`}
          >
            {showReprintButton && hasReprintAPI && (isRootPath || (isResultPage && !isFullscreen)) && (
              <button
                type="button"
                title={t('reprint.title', 'Reprint')}
                onClick={handleOpenReprint}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
              >
                <FaPrint
                  className={`${iconClass} ${
                    fullscreenPinGraceOpacity
                      ? 'opacity-80 animate-pulse'
                      : fullscreenPinIdleOpacity
                        ? 'opacity-55'
                        : ''
                  }`}
                />
              </button>
            )}

            {isRootPath && (!isFullscreen || !password) && (
              <button
                type="button"
                title={t('controls.settings', 'Settings')}
                onClick={handleOpenSettings}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
              >
                <FaGear className={iconClass} />
              </button>
            )}
            <button
              type="button"
              title={
                isFullscreen
                  ? t('controls.exitFullscreen', 'Exit Fullscreen')
                  : t('controls.enterFullscreen', 'Enter Fullscreen')
              }
              onClick={handleFullscreenToggle}
              className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
            >
              {isFullscreen ? (
                <FaLock
                  className={`${iconClass} ${
                    password
                      ? fullscreenPinGraceOpacity
                        ? 'opacity-85 animate-pulse'
                        : fullscreenPinIdleOpacity
                          ? 'opacity-55'
                          : ''
                      : isStartupGracePeriodActive
                        ? 'opacity-75 animate-pulse'
                        : 'opacity-55'
                  }`}
                />
              ) : (
                <FaLockOpen className={`${iconClass} animate-pulse`} />
              )}
            </button>
            {isReprintPaymentActive && (
              <button
                type="button"
                title={t('controls.skipPayment', 'Skip Payment')}
                onClick={() => setShowReprintBypassPinModal(true)}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
                id="reprint-bypass-btn"
              >
                <FaMoneyBillWave className={`${iconClass} opacity-55`} />
              </button>
            )}
            {/*
              Operator-bypass Skip Payment chrome is available on both the
              Payment page and Checkout. In voucher-only regions the
              customer never reaches Payment, so without this affordance
              on Checkout the operator would have no PIN entry point at
              all to fall back on.
            */}
            {(isPaymentPage || isCheckoutPage) && (
              <button
                type="button"
                title={t('controls.skipPayment', 'Skip Payment')}
                onClick={() => setShowSkipPaymentModal(true)}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
              >
                <FaMoneyBillWave className={`${iconClass} opacity-55`} />
              </button>
            )}
            {!isRootPath && (
              <button
                type="button"
                title={t('controls.exitSession', 'Exit Session')}
                onClick={() => setShowExitModal(true)}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea} ${
                  hideExitButton ? 'opacity-10' : ''
                }`}
              >
                <FaTimes className={iconClass} />
              </button>
            )}
          </div>
        </div>
      </div>
        </>
      ) : null}
      <PinModal
        isOpen={showSubmitPinModal}
        onClose={() => setShowSubmitPinModal(false)}
        onSubmit={handlePinSubmit}
        title={t('controls.exitFullscreen', 'Exit Fullscreen')}
        subtitle={t('controls.enterPin', 'Enter 6-digit PIN to exit fullscreen-mode')}
      />
      <PinModal
        isOpen={showSkipPaymentModal}
        onClose={() => setShowSkipPaymentModal(false)}
        onSubmit={handleSkipPayment}
        title={t('controls.skipPayment', 'Skip Payment')}
        subtitle={t('controls.skipPaymentPin', 'Enter 6-digit PIN to skip payment')}
      />
      
      {/* PIN Modal for accessing Reprint */}
      <PinModal
        isOpen={showReprintPinModal}
        onClose={() => setShowReprintPinModal(false)}
        onSubmit={(pin) => {
          if (pin === password) {
            setShowReprintPinModal(false)
            setShowReprintModal(true)
          } else {
            cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
          }
        }}
        title={t('reprint.pinRequired', 'PIN Required')}
        subtitle={t('reprint.enterPinToAccess', 'Enter PIN to access reprint menu')}
      />

      <PinModal
        isOpen={showReprintBypassPinModal}
        onClose={() => setShowReprintBypassPinModal(false)}
        onSubmit={handleReprintBypass}
        title={t('controls.skipPayment', 'Skip Payment')}
        subtitle={t('controls.skipPaymentPin', 'Enter 6-digit PIN to skip payment')}
      />

      <ReprintModal
        isOpen={showReprintModal}
        onClose={() => setShowReprintModal(false)}
      />
      <ConfirmModal
        isOpen={showExitModal}
        onClose={() => setShowExitModal(false)}
        onConfirm={async () => {
          setIsQuitting(true)
          setShowExitModal(false)
          try {
            await sessionReset()
          } catch (error) {
            console.error('[CONTROLS] Error during session reset:', error)
          } finally {
            setIsQuitting(false)
          }
        }}
        title={t('controls.cancelSession', 'Cancel Session')}
        message={t('controls.cancelConfirm', 'Are you sure you want to cancel this session?')}
      />
      <ProcessingModal
        isVisible={isQuitting}
        title={t('controls.quitting', 'Ending Session...')}
        message={t('controls.quittingMessage', 'Please wait while we save your session data')}
        primaryColor={primaryColor}
      />
    </>
  )
}

export default ControlButtons
